import { Box, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

interface UtmFinalLinkPreviewProps {
  // The final URL with the applied UTM query already merged in.
  previewUrl: string;
  // The query string Fluenceur appends (no leading `?`), or null when nothing is
  // added. Used to locate the added part inside `previewUrl`.
  urlTags: string | null;
}

/**
 * "Final link" preview: the brand's URL in muted grey, and the query Fluenceur
 * appends in the positive accent colour.
 *
 * The added part is located by searching `urlTags` inside the final URL rather
 * than by comparing prefixes: the query is inserted BEFORE any `#fragment`, so a
 * destination carrying a fragment is not a prefix of its own preview and the
 * highlight would otherwise disappear. Whatever follows the appended query (the
 * fragment) is rendered muted again.
 */
export function UtmFinalLinkPreview({ previewUrl, urlTags }: UtmFinalLinkPreviewProps) {
  const { t } = useTranslation();

  // The separator (`?` or `&`) is part of what was added: it only exists because
  // Fluenceur appended something, hence the `queryIndex - 1`.
  const queryIndex = urlTags ? previewUrl.indexOf(urlTags) : -1;
  const hasAppended = urlTags !== null && queryIndex > 0;
  const base = hasAppended ? previewUrl.slice(0, queryIndex - 1) : previewUrl;
  const appended = hasAppended ? previewUrl.slice(queryIndex - 1, queryIndex + urlTags.length) : '';
  const trailing = hasAppended ? previewUrl.slice(queryIndex + urlTags.length) : '';

  return (
    <Box
      bg="bg.muted"
      border="1px solid"
      borderColor="border.muted"
      borderRadius="md"
      px={3}
      py={2.5}
    >
      <Text
        fontSize="2xs"
        fontWeight="medium"
        color="fg.muted"
        textTransform="uppercase"
        letterSpacing="wider"
        mb={1.5}
      >
        {t('mentions.boost.setup.utm.previewLabel')}
      </Text>
      <Text fontSize="xs" fontFamily="mono" wordBreak="break-all" lineHeight="1.6">
        <Text as="span" color="fg.muted">
          {base}
        </Text>
        {appended && (
          <Text as="span" color="green.solid" fontWeight="medium">
            {appended}
          </Text>
        )}
        {trailing && (
          <Text as="span" color="fg.muted">
            {trailing}
          </Text>
        )}
      </Text>
    </Box>
  );
}
